// Lib
// Types
import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';

import type { SocialProfileShortType, SocialProfileType } from '@/types/socialProfileType';
import type {
    GroupAnalyticsDataApiAnalyticsType,
    GroupAnalyticsDataApiSocialProfilesPerformanceType
} from '@/utils/hooks/swr/group/useGroupAnalytics/types';

import type { ChartDataEntryType, ChartType } from '@/lib/analytics/charts/type';
import { formatSocialProfileType } from '@/lib/socialProfile';

export const comparativeViewColorPicker = [
    'blue',
    'green',
    'red',
    'aquablue',
    'yellow',
    'purple',
    'orange',
    'gray',
    'pink'
] as const;

type ChartSocialProfilesDisplayColor = (typeof comparativeViewColorPicker)[number];

type ChartSocialProfileDisplayName = `${SocialProfileType['slug']} (${SocialProfileShortType})`;

export type ChartSocialProfilesDisplayInfosType = Record<
    SocialProfileType['id'],
    {
        color: ChartSocialProfilesDisplayColor;
        name: ChartSocialProfileDisplayName;
    }
>;

type ChartComparativeViewConfig = ChartType<any> & {
    color: Record<SocialProfileType['id'], ChartSocialProfilesDisplayColor>;
    name: Record<SocialProfileType['id'] | string, string | { raw: ChartSocialProfileDisplayName }>;
};

export const getChartSocialProfilesDisplayInfos = (
    socialProfiles: SocialProfileType[]
): ChartSocialProfilesDisplayInfosType => {
    return socialProfiles?.reduce(
        (acc, sp, index) => ({
            ...acc,
            [sp.id]: {
                color: comparativeViewColorPicker[index % comparativeViewColorPicker.length],
                name: `${sp.slug} (${formatSocialProfileType(sp)})`
            }
        }),
        {}
    );
};

export const getChartCumulativeViewData = (
    analytics: GroupAnalyticsDataApiAnalyticsType,
    groupId: SpGroupType['id'],
    config: ChartType<any>
) => {
    const groupData = analytics?.find((analyticsItem) => analyticsItem.group === groupId);
    if (!groupData) {
        return {};
    }
    return groupData[config.id];
};

export const getChartComparativeViewData = (
    analytics: GroupAnalyticsDataApiAnalyticsType,
    mostEfficientSocialProfiles: GroupAnalyticsDataApiSocialProfilesPerformanceType,
    config: ChartType<any>
) => {
    if (!mostEfficientSocialProfiles) {
        return {};
    }
    const spsData = analytics.filter(
        (analyticsItem) =>
            analyticsItem.social_profile &&
            mostEfficientSocialProfiles[config.id].includes(analyticsItem.social_profile)
    );

    return {
        graphData: spsData
            .reduce(
                (acc, current) => [
                    ...acc,
                    ...(current[config.id]?.['graphData']?.map(
                        (item: ChartDataEntryType) => item.date
                    ) ?? [])
                ],
                []
            )
            .filter((value, index, array) => array.indexOf(value) === index)
            .sort((d1, d2) => new Date(d1).getTime() - new Date(d2).getTime())
            .map((date) =>
                spsData.reduce(
                    (acc, current) => ({
                        ...acc,
                        [current?.social_profile]:
                            current?.[config.id]?.['graphData']?.find(
                                (entry: ChartDataEntryType) => entry.date === date
                            )?.value ?? null
                    }),
                    {
                        date
                    }
                )
            )
    };
};

export const getChartComparativeViewConfig = (
    mostEfficientSocialProfiles: GroupAnalyticsDataApiSocialProfilesPerformanceType,
    config: ChartType<any>,
    socialProfilesDisplayInfos: ChartSocialProfilesDisplayInfosType
) => {
    return mostEfficientSocialProfiles?.[config.id].reduce(
        (acc: ChartComparativeViewConfig, spId: SocialProfileType['id']) => ({
            ...acc,
            color: { ...acc.color, [spId]: socialProfilesDisplayInfos?.[spId]?.color },
            name: {
                ...acc.name,
                [spId]: {
                    raw: socialProfilesDisplayInfos?.[spId]?.name
                }
            }
        }),
        { ...config, color: {}, name: { date: 'date' } }
    );
};
